<script setup>
import { computed, ref } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import UiSegmented from '@/Components/UI/UiSegmented.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import UiButton from '@/Components/UI/UiButton.vue'

const { t } = useI18n()
const page = usePage()

const props = defineProps({
  current: { type: String, default: 'this_month' },
  from:    { type: String, default: '' },
  to:      { type: String, default: '' },
})

const periods = ['today', 'this_week', 'this_month', 'last_month', 'last_3_months', 'this_year', 'custom']
const options = computed(() => periods.map((p) => ({ value: p, label: t(`dashboard.period.${p}`) })))
const showCustom = ref(props.current === 'custom')
const customFrom = ref(props.from || '')
const customTo   = ref(props.to   || '')

const setCookie = (k, v) => {
  const yr = 60 * 60 * 24 * 365
  if (v == null || v === '') {
    document.cookie = `${k}=; path=/; max-age=0; samesite=lax`
  } else {
    document.cookie = `${k}=${encodeURIComponent(v)}; path=/; max-age=${yr}; samesite=lax`
  }
}

const navigate = (params) => {
  setCookie('period',    params.period)
  setCookie('date_from', params.date_from)
  setCookie('date_to',   params.date_to)

  const url = new URL(page.url, window.location.origin)
  const path = url.pathname
  const existing = Object.fromEntries(url.searchParams.entries())
  delete existing.period
  delete existing.date_from
  delete existing.date_to
  delete existing.page
  router.get(path, { ...existing, ...params }, { preserveState: true, preserveScroll: true })
}

const select = (p) => {
  if (p === 'custom') { showCustom.value = true; return }
  showCustom.value = false
  if (p === props.current) return
  navigate({ period: p })
}

const applyCustom = () => {
  if (!customFrom.value || !customTo.value) return
  navigate({ period: 'custom', date_from: customFrom.value, date_to: customTo.value })
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-2">
    <UiSegmented
      :model-value="current"
      :options="options"
      @update:model-value="select"
    />

    <div v-if="showCustom || current === 'custom'" class="flex items-center gap-2">
      <UiInput v-model="customFrom" type="date" :aria-label="t('dashboard.period.from')" />
      <span class="text-xs text-[color:var(--muted)]">{{ t('dashboard.period.to') }}</span>
      <UiInput v-model="customTo" type="date" :aria-label="t('dashboard.period.toDate')" />
      <UiButton variant="primary" size="sm" @click="applyCustom">
        {{ t('dashboard.period.apply') }}
      </UiButton>
    </div>
  </div>
</template>
